import { Component, OnDestroy } from '@angular/core';
import { TokenService } from '@auth';
import { GridDataResult, DataStateChangeEvent,
  FilterService,} from '@progress/kendo-angular-grid';
import { GroupDescriptor, CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { ColumnOptions, GridState, GridStateCompositeFilterDescriptor, LoaderService, MessageService, NamedEntity } from '@shared';
import { ChangesetService, GlobalEventsService, TiersService } from '@shared-data';
import { GridRowChangesetSummary } from '../../models';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';

@Component({
  selector: 'app-history-summary',
  templateUrl: './history-summary.component.html',
  styleUrl: './history-summary.component.scss'
})
export class HistorySummaryComponent implements OnDestroy {

  changesetSummary: GridDataResult = { data: [], total: 0 };

  // Opening the detail of one sync also destroys this component, and that is the user drilling
  // in rather than walking away from the failures, so it must not offer to dismiss the alert.
  private navigatingToDetails = false;

  pageSize = 10;
  state = new GridState(this.pageSize);
  columns: ColumnOptions[] = [];

  fontSize: number = 0.875;

  fromDate: Date = new Date(new Date().setDate(new Date().getDate() - 7));
  thruDate: Date = new Date();
  group: GroupDescriptor[] = [{ field: 'transactionDate', dir: 'desc' }];

  isAvailabilityOnly: boolean = false;
  selectedItemIds: number[] = [];
  selectedTypes: NamedEntity[] = [];
  modifierCodesEnabled: boolean = environment.modifierCodesFeatureEnabled;

  tiers: NamedEntity[] = [];
  selectedTier: NamedEntity = new NamedEntity('-1', 'All Tiers');

  types: NamedEntity[] = this.modifierCodesEnabled ? [
      new NamedEntity('pricing', 'Pricing'),
      new NamedEntity('availability', 'Availability'),
      new NamedEntity('realignment', 'Realignment'),
      new NamedEntity('modifiercodes', 'Modifier Codes'),
    ] : [
      new NamedEntity('pricing', 'Pricing'),
      new NamedEntity('availability', 'Availability'),
      new NamedEntity('realignment', 'Realignment'),
    ];
    

    distinctParams?: Record<string, string | string[]>;

  get customFilterView(): string {
    return 'history-summary';
  }

  filterCache: Map<string, string[]> = new Map<string, string[]>();

  onCustomFilterChange(
    column: string,
    event: string[],
    filterService: FilterService,
  ) {
    this.filterCache.set(column, event);

    filterService.filter({
      filters: event.map((value) => ({
        field: column,
        operator: 'eq',
        value,
      })),
      logic: 'or',
    });
  }

  filterChanged(event: CompositeFilterDescriptor) {
    if (event.filters.length === 0) {
      this.filterCache.clear();
    }

    for (const field of this.filterCache.keys()) {
      if (!GridState.hasField(event, field)) {
        this.filterCache.delete(field);
      }
    }
  }

  constructor(
      private changesetService: ChangesetService,
      private messageService: MessageService,
      private loaderService: LoaderService,
      private tokenService: TokenService,
      private tierService: TiersService,
      private router: Router,
      private globalEventsService: GlobalEventsService
    ) {
    }

  // Leaving this page is what tells the alerts banner the failures have been reviewed, the same
  // way the LTO alert is acknowledged on the way out of the LTO grid. Whether there is anything
  // to dismiss is decided by the banner, which is the one holding the count.
  ngOnDestroy(): void {
    if (!this.navigatingToDetails) {
      this.globalEventsService.onNavigateAwayFromSyncHistory();
    }
  }
  

  async ngOnInit() {
    this.isAvailabilityOnly = await this.tokenService.isAvailabilityOnly();

    // For AvailabilityOnly users, filter types to only show Availability
    if (this.isAvailabilityOnly) {
      this.types = [new NamedEntity('availability', 'Availability')];
      this.selectedTypes = [...this.types];
    }

    this.columns = new GridRowChangesetSummary()
        .getColumns()

    const viewDetailsColumn = new ColumnOptions('viewDetails', '', 9);
    viewDetailsColumn.filterable = false;
    viewDetailsColumn.sortable = false;
    viewDetailsColumn.width = 80;
    viewDetailsColumn.visibleInQuickView = true;
    this.columns.push(viewDetailsColumn);

    if (sessionStorage.getItem('historySummaryQueryParams')) {
      const savedParams = JSON.parse(sessionStorage.getItem('historySummaryQueryParams')!);
      this.state = new GridState(savedParams.state.take, savedParams.state.sort, undefined, undefined, undefined);
      this.fromDate = new Date(savedParams.fromDate);
      this.thruDate = new Date(savedParams.thruDate);
      this.selectedItemIds = savedParams.selectedItemIds;
      this.selectedTypes = this.types.filter(type => savedParams.selectedTypes.includes(type.id));
      this.selectedTier = this.tiers.find(tier => tier.id === savedParams.selectedTierId) || new NamedEntity('-1', 'All Tiers');
    }

    this.getTiers();
  }

  onDataStateChanged(event: DataStateChangeEvent) {
    this.fetchChangesetSummary(event as GridState);
  }

  setDefaultState() {
    this.filterCache.clear();
    this.state = new GridState(GridState.getPageSizeByFontSize(this.fontSize));

    this.fetchChangesetSummary(this.state);
  }

  onItemFilterChanged(itemIds: number[]) {
    this.selectedItemIds = itemIds;
    this.state.skip = 0;
    this.fetchChangesetSummary(this.state);
  }

  getTiers() {
    this.loaderService.show();

    this.tierService.getTiers().subscribe({
      next: (tiers) => {
        this.tiers = tiers.map((tier) => new NamedEntity(tier.id, tier.name));
        this.tiers.unshift(new NamedEntity('-1', 'All Tiers'));
        this.selectedTier = this.tiers[0];
        this.loaderService.hide();

        this.fetchChangesetSummary(this.state);
      },
      error: (err) => {
        this.messageService.error(`Failed to get tiers: ${err.error}`);
        this.loaderService.hide();
      },
    });
  }

  fetchChangesetSummary(state: GridState) {
    this.loaderService.show();
    if (state.filter !== undefined) {
      this.state.filter = state.filter;
    }
    if (state.skip !== undefined) {
      this.state.skip = state.skip;
    }
    if (state.take !== undefined) {
      this.state.take = state.take;
    }
    if (state.sort?.length) {
      this.state.sort = state.sort;
    } else {
      this.state.sort = [{ field: 'transmittedDate', dir: 'desc' }];
    }

    if (this.state.filter) {
      this.state.filter = new GridStateCompositeFilterDescriptor(
        GridState.removeFilterByFields(
          this.state.filter,
          'type',
          'tierId',
          'restaurantId',
        ),
      );
    }

    if (this.selectedTier.id != '-1') {
      this.state.filter = this.state.filter?.and({
        field: 'tierId',
        operator: 'eq',
        value: this.selectedTier.id,
      });
    }


    if (this.group) {
      this.state.group = this.group;
    }

    this.distinctParams = {
      fromDate: this.fromDate.toISOString(),
      thruDate: this.thruDate.toISOString(),
      itemIds: this.selectedItemIds.map(String),
      syncTypes: this.selectedTypes.map((type) => type.id),
    };

    this.changesetService
      .getChangesetSummary(
        this.state,
        this.fromDate,
        this.thruDate,
        this.selectedItemIds,
        this.selectedTypes.map((type) => type.id)
      )
      .subscribe({
        next: (changesets) => {
          this.changesetSummary = changesets;

          this.loaderService.hide();
        },
        error: () => {
          this.messageService.error('Failed to load changesets');
          this.loaderService.hide();
        },
      });
  }

  getTotal(group: any, field: string): number {
    let total = 0;
    if (group && group.items) {
      for (const item of group.items) {
        if (item instanceof Object && field in item) {
          total += item[field] || 0;
        }
      } 
    }
    return total;
  }

  navigateToDetails(row: GridRowChangesetSummary, syncStatus?: string) {
    this.navigatingToDetails = true;
    const queryParams: any = {
      tierId: row.tierId,
      transactionDate: row.transactionDate,
      submittedBy: row.submittedBy,
      scheduledDate: row.scheduledDate,
      transmittedDate: row.transmittedDate,
      syncStatus: syncStatus || 'all'
    };
    
    if (this.selectedItemIds.length > 0) {
      queryParams.itemIds = this.selectedItemIds.join(',');
    }

    if (row.syncType?.length) {
      queryParams.types = row.syncType.join(',');
    }

    sessionStorage.setItem('historySummaryQueryParams', JSON.stringify({
      state: this.state, 
      fromDate: this.fromDate,
      thruDate: this.thruDate,
      selectedItemIds: this.selectedItemIds,
      selectedTypes: this.selectedTypes.map((type) => type.id),
      selectedTierId: this.selectedTier.id
    }));

    this.router.navigate(['/reports/history'], { queryParams });
  }
}
